"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { track } from "@vercel/analytics";
import { useLoaderState } from "@/context/LoaderContext";
import { useMotionMode } from "@/context/MotionContext";
import { Magnetic } from "./Magnetic";
import { LockIcon, UnlockIcon } from "./Button";
import type { Project } from "@/data/projects";

// Protected content returned from API
export interface ProtectedContent {
  squad?: Project["squad"];
  heroDescription?: string;
  heroImage?: string;
  heroVideo?: string;
  heroCaption?: string;
  keyImpact?: string;
  keyImpactImage?: string;
  keyImpactCaption?: string;
  challenge?: string;
  challengeImage?: string;
  challengeImages?: { src: string; caption?: string }[];
  challengeImageCaption?: string;
  solution?: string;
  solutionMedia?: Project["solutionMedia"];
  demoMedia?: Project["demoMedia"];
  process?: Project["process"];
  processApproach?: Project["processApproach"];
  userQuotes?: Project["userQuotes"];
  teamQuotes?: Project["teamQuotes"];
  results?: Project["results"];
  resultsCaption?: string;
  ratingsComparison?: Project["ratingsComparison"];
  ratingsComparisonCaption?: string;
  recognitionImages?: Project["recognitionImages"];
  conclusionImages?: Project["conclusionImages"];
  learnings?: string[];
  conclusion?: string;
}

interface PasswordModalProps {
  project: Project;
  onSuccess: (projectId: string, content: ProtectedContent) => void;
  onClose: () => void;
  isWhiteTheme?: boolean;
  isLightMode?: boolean;
  // Validation endpoint. Defaults to the case-study route; experiments
  // override this to their own thin validator.
  endpoint?: string;
  // Where the "Home" escape hatch links. Case studies go to /work,
  // experiments go to /exp.
  homeHref?: string;
  // Label for that escape hatch. Defaults to "Home"; experiments pass
  // "Experiments" so it's clear back goes to /exp, not the site root.
  homeLabel?: string;
}

export function PasswordModal({
  project,
  onSuccess,
  onClose,
  isWhiteTheme = false,
  isLightMode = false,
  endpoint,
  homeHref = "/work",
  homeLabel = "Home",
}: PasswordModalProps) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [buttonHover, setButtonHover] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track previous overflow to restore on close (case study keeps it hidden)
  const previousOverflowRef = useRef<string>("");

  // Get loader state for coordinated animation timing
  const { isLoaderActive, consumeLoaderDelay } = useLoaderState();
  const { canAnimate, mounted: motionMounted } = useMotionMode();

  // Track if we've run the entrance animation
  const hasAnimatedRef = useRef(false);

  // Animate in on mount with GSAP (matches PageEnter timing)
  useEffect(() => {
    previousOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Wait for loader to complete
    if (isLoaderActive) return;

    // Wait for motion context to initialize (determines animation approach)
    if (!motionMounted) return;

    // Only animate once
    if (hasAnimatedRef.current) return;
    hasAnimatedRef.current = true;

    const container = containerRef.current;
    if (!container) return;

    // Get delay and consume it (so other components get 0)
    const delay = consumeLoaderDelay();

    const runAnimation = async () => {
      if (!canAnimate) {
        // No GSAP - just show immediately
        setIsVisible(true);
        inputRef.current?.focus();
        return;
      }

      try {
        const gsap = (await import("gsap")).default;

        gsap.fromTo(
          container,
          { opacity: 0, scale: 0.93 },
          {
            opacity: 1,
            scale: 1,
            duration: 0.7,
            ease: "power2.out",
            delay: delay / 1000,
            onStart: () => setIsVisible(true),
            onComplete: () => {
              // Clear inline transform to prevent any stacking context issues
              container.style.transform = "";
              // Focus input after animation completes
              inputRef.current?.focus();
            },
          }
        );
      } catch {
        // GSAP failed to load - fall back to immediate visibility
        setIsVisible(true);
        inputRef.current?.focus();
      }
    };

    runAnimation();

    return () => {
      document.body.style.overflow = previousOverflowRef.current;
    };
  }, [isLoaderActive, consumeLoaderDelay, canAnimate, motionMounted]);

  const handleClose = useCallback(async () => {
    setIsExiting(true);
    const container = containerRef.current;

    if (canAnimate && container) {
      const gsap = (await import("gsap")).default;
      gsap.to(container, {
        opacity: 0,
        duration: 0.3,
        ease: "power2.out",
        onComplete: () => {
          document.body.style.overflow = previousOverflowRef.current;
          onClose();
        },
      });
    } else {
      document.body.style.overflow = previousOverflowRef.current;
      onClose();
    }
  }, [onClose, canAnimate]);

  // Convert project brand color to RGB for rgba() usage
  const hexToRgb = (hex: string): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : "255, 94, 0"; // fallback to orange
  };

  const brandColor = project.brandColor || '#FF5E00';
  const brandRgb = hexToRgb(brandColor);

  // Focus trap - keep focus within modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        handleClose();
        return;
      }

      if (e.key === "Tab" && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    },
    [handleClose]
  );

  useEffect(() => {
    // Focus input on mount
    inputRef.current?.focus();

    // Add keyboard listener
    document.addEventListener("keydown", handleKeyDown);

    // Store previously focused element to restore on close
    const previouslyFocused = document.activeElement as HTMLElement;

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [handleKeyDown]);

  const [isLoading, setIsLoading] = useState(false);
  const [cooldownSec, setCooldownSec] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  // Countdown timer for cooldown - clear error when cooldown expires
  useEffect(() => {
    if (cooldownSec <= 0) return;
    const timer = setTimeout(() => {
      setCooldownSec((s) => {
        const next = s - 1;
        if (next <= 0) {
          setError(false);
          setErrorMessage("");
        }
        return next;
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [cooldownSec]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading || cooldownSec > 0) return;

    setIsLoading(true);
    setError(false);
    setErrorMessage("");

    try {
      const response = await fetch(endpoint ?? `/api/projects/${project.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: password.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        track("password_unlock", { project: project.id, success: true });
        // Animate out then call onSuccess
        setIsExiting(true);
        const container = containerRef.current;
        if (canAnimate && container) {
          const gsap = (await import("gsap")).default;
          gsap.to(container, {
            opacity: 0,
            scale: 2,
            duration: 0.55,
            ease: "expo.in",
            onComplete: () => {
              document.body.style.overflow = previousOverflowRef.current;
              onSuccess(project.id, data.content);
            },
          });
        } else {
          document.body.style.overflow = previousOverflowRef.current;
          onSuccess(project.id, data.content);
        }
      } else {
        track("password_unlock", { project: project.id, success: false });
        setError(true);
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
        setPassword("");

        // Handle rate limiting with backoff
        if (data.retryAfterMs && data.retryAfterMs > 0) {
          setCooldownSec(Math.ceil(data.retryAfterMs / 1000));
        }
        setErrorMessage(data.error || "Incorrect password");
      }
    } catch {
      setError(true);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      setErrorMessage("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const showModal = isVisible && !isExiting;

  return (
    <div
      ref={containerRef}
      onClick={handleClose}
      data-lenis-prevent
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center p-[var(--section-padding-x)]"
      style={{
        // Start hidden (opacity: 0, scale: 0.93), animate to visible
        // GSAP controls animation when enabled, CSS transition when disabled
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'scale(0.93)',
        backgroundColor: showModal ? 'rgba(0,0,0,0.85)' : 'rgba(0,0,0,0)',
        backdropFilter: showModal ? 'blur(4px)' : 'blur(0px)',
        WebkitBackdropFilter: showModal ? 'blur(4px)' : 'blur(0px)',
        // When GSAP, disable CSS transitions (GSAP handles it). Otherwise CSS.
        transition: canAnimate ? 'none' : 'all 0.3s ease',
      }}
      role="presentation"
    >
      {/* Bottom scrim for legibility on weird viewports */}
      <div
        className="absolute inset-x-0 bottom-0 h-[50vh] pointer-events-none z-0"
        style={{
          background: `linear-gradient(to top, rgba(${brandRgb}, 0.15) 0%, rgba(${brandRgb}, 0.05) 40%, transparent 100%)`,
        }}
      />
      {/* Protected indicator above the pill */}
      <div className="text-center mb-6">
        {/* Badge with lock icon */}
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-3"
          style={{
            backgroundColor: `rgba(${brandRgb}, 0.15)`,
            border: `1px solid rgba(${brandRgb}, 0.3)`,
          }}
        >
          <span style={{ color: brandColor }}><LockIcon size={14} /></span>
          <span
            className="text-xs uppercase tracking-[0.15em] font-black"
            style={{ color: brandColor }}
          >
            Protected
          </span>
        </div>
        {/* Project title */}
        <p
          id="modal-description"
          className="font-headline text-[clamp(1.25rem,3vw,1.5rem)] font-bold"
          style={{ color: brandColor }}
        >
          {project.title}
        </p>
      </div>

      {/* Pill-shaped input with embedded button */}
      <div
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        className="w-full max-w-[400px]"
        style={{
          animation: isShaking ? "shake var(--duration-slow) ease" : "none",
        }}
      >
        <form onSubmit={handleSubmit} className="relative">
          <label htmlFor="password-input" className="sr-only">
            Password
          </label>
          <div
            className="glass relative flex items-center rounded-full overflow-hidden"
            data-theme="dark"
            style={{
              isolation: 'isolate',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              transition: "all var(--duration-fast) var(--ease-out)",
              backgroundColor: 'rgba(20,20,20,0.85)',
              border: `1px solid ${
                error
                  ? "rgba(255,80,80,0.5)"
                  : isFocused
                  ? `rgba(${brandRgb}, 0.5)`
                  : "rgba(255,255,255,0.12)"
              }`,
              boxShadow: isFocused && !error
                ? `0 0 0 3px rgba(${brandRgb}, 0.15)`
                : "none",
            }}
          >
            <input
              ref={inputRef}
              id="password-input"
              type="password"
              value={password}
              disabled={cooldownSec > 0}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(false);
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="Enter password"
              aria-describedby={error ? "password-error" : undefined}
              aria-invalid={error}
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              className="flex-1 pl-5 pr-3 py-4 bg-transparent text-white placeholder:text-[rgba(255,255,255,0.4)] text-base"
              style={{ outline: 'none' }}
            />

            {/* Unlock button inside */}
            <button
              type="submit"
              disabled={isLoading || cooldownSec > 0}
              className="h-10 px-5 mr-2 rounded-full text-[0.85rem] font-medium cursor-pointer flex items-center gap-2 disabled:opacity-70 disabled:cursor-wait"
              style={{
                backgroundColor: (password.length > 0 || buttonHover) ? brandColor : `rgba(${brandRgb}, 0.25)`,
                color: (password.length > 0 || buttonHover) ? '#000' : brandColor,
                transition: "all var(--duration-slow) var(--ease-out)",
              }}
              onMouseEnter={() => setButtonHover(true)}
              onMouseLeave={() => setButtonHover(false)}
            >
              {isLoading ? (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  className="animate-spin"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                </svg>
              ) : (
                <span
                  style={{
                    display: 'flex',
                    transform: password.length > 0 ? 'scale(1.2)' : 'scale(1)',
                    transition: 'transform var(--duration-fast) var(--ease-out)',
                  }}
                >
                  {password.length > 0 ? <UnlockIcon size={16} /> : <LockIcon size={16} />}
                </span>
              )}
            </button>
          </div>

          {(error || cooldownSec > 0) && (
            <p
              id="password-error"
              role="alert"
              aria-live="polite"
              className="text-[rgba(255,80,80,0.9)] text-[0.8rem] mt-3 text-center"
            >
              {cooldownSec > 0
                ? `Wait ${cooldownSec}s before trying again`
                : errorMessage || "Incorrect password"}
            </p>
          )}

          {/* Password hint */}
          {project.passwordHint && (
            <p className="text-[rgba(255,255,255,0.5)] text-sm mt-3 text-center italic">
              Hint: {project.passwordHint}
            </p>
          )}
        </form>
      </div>

      {/* Home button below - force dark theme since scrim is always dark */}
      <div data-theme="dark" className="mt-6">
        <Magnetic strength={0.15} tiltStrength={6}>
          <a
            href={homeHref}
            className="flex items-center justify-center cursor-pointer hover:scale-105 glass px-5 py-2.5 rounded-full"
            style={{
              isolation: "isolate",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
            }}
          >
            <span className="flex items-center gap-2 text-sm text-[rgba(255,255,255,0.7)]">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {homeLabel}
            </span>
          </a>
        </Magnetic>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%,
          100% {
            transform: translateX(0);
          }
          20% {
            transform: translateX(-10px);
          }
          40% {
            transform: translateX(10px);
          }
          60% {
            transform: translateX(-10px);
          }
          80% {
            transform: translateX(10px);
          }
        }
      `}</style>
    </div>
  );
}
